/**
 * The colour themes, one per business website.
 *
 * # WHERE THE CHOICE LIVES
 *
 * Not in admin any more. Each vertical is fixed to one theme by `THEME` in its
 * `.env`, validated by the Go `theme` package, and handed to the page by the
 * server in `runtimeConfig.theme` -- already resolved, so a vertical that sets
 * none arrives with its company type's default and the first frame is painted in
 * the right colour. This file only turns an id into a palette.
 *
 * `go/internal/core/theme/theme.go` holds the same ids and the per-type
 * defaults; `__tests__/themes.test.ts` fails when the two lists drift.
 *
 * # WHERE THE COLOURS CAME FROM
 *
 * The businesses' own websites (the teisyovision repo's public/css/style.css),
 * so a booking site reads as part of its business's site:
 *
 * - `primary` is the action colour -- buttons, links, accents. The sites' own
 *   orange `#e85d04` measures 3.5:1 against white, so it is darkened to the
 *   site's `--color-primary-dark` `#c44e03` (4.7:1); every `primary` here
 *   carries white text at WCAG AA (4.5:1) or better, and the tests hold that.
 * - `frame` is the colour that frames the page: the header bar and the home
 *   hero band, matching the sites' header and footer (`--color-frame`). White
 *   text sits on it too, so it is held to the same 4.5:1.
 */

export interface ThemeDef {
  id: string;
  primary: string;
  /** Absent means MUI's own secondary. */
  secondary?: string;
  /** Header bar and hero band. Absent means the primary, as stock Material has it. */
  frame?: string;
  /**
   * The website's own `--color-primary` / `--color-primary-dark`, used ONLY by
   * the website header (SiteHeader) for its button and its link underline, so
   * that bar is the website's bar to the pixel. Not held to 4.5:1 like
   * `primary`: it copies the site as it is, and the site's orange is 3.5:1.
   */
  site?: { main: string; dark: string };
  /**
   * The admin's page colour behind its white cards, when the website's own
   * ground differs from the cool grey default: the 玉出 and 光明池 sites sit on
   * warm paper, and their admin with them.
   */
  ground?: string;
  /** Company types that start on this theme. Mirrors `byType` in theme.go. */
  defaultFor: readonly string[];
}

export const MATERIAL_THEME_ID = "material";

/** The order of `IDs` in theme.go. */
export const THEMES: readonly ThemeDef[] = [
  {
    // 玉出: teisyogymseikotsuin-tamade.teisyovision.com -- orange on charcoal.
    id: "teisyo-tamade",
    primary: "#c44e03",
    frame: "#2b2b2b",
    site: { main: "#e85d04", dark: "#c44e03" },
    ground: "#f6f5f3",
    defaultFor: ["CLINIC", "GYM"],
  },
  {
    // 光明池: teisyogymseikotsuin-komyoike.teisyovision.com -- blue on navy.
    id: "teisyo-komyoike",
    primary: "#0c6cb4",
    frame: "#084a7c",
    site: { main: "#0c6cb4", dark: "#08528a" },
    ground: "#f6f5f3",
    defaultFor: [],
  },
  {
    // nicorirealestate.com.
    id: "nicori-navy",
    primary: "#253654",
    secondary: "#06c755",
    defaultFor: ["REAL_ESTATE"],
  },
  {
    id: MATERIAL_THEME_ID,
    // MUI's own primary.main, listed for completeness. The theme itself passes
    // no palette for this id, so it is exactly stock.
    primary: "#1976d2",
    defaultFor: [],
  },
];

/**
 * The theme for an id, or Material for anything unknown or absent.
 *
 * Absent is the normal case under Vite dev and every e2e stack, which run no
 * SPA listener to template the id in -- so those keep the stock palette their
 * screenshot baselines were taken with.
 */
export function findTheme(id: string | null | undefined): ThemeDef {
  return (
    THEMES.find((t) => t.id === id) ??
    (THEMES.find((t) => t.id === MATERIAL_THEME_ID) as ThemeDef)
  );
}
